/**
 * overseer.ts — m31 P2 (O2 / O2b / O3 client half / O5): browser wiring for Overseer, C813's product analytics.
 *
 * The ONLY file that touches the vendored SDK (src/vendor/overseer-sdk) and rrweb. Both are `import()`ed lazily inside
 * initOverseer, so neither is in the main bundle (scripts/check-overseer-chunks.cjs enforces that after every build).
 * Free of `import.meta` so jest can load it (see utils/promoLabel.ts).
 *
 * Events go to OUR same-origin proxy (`/api/ov/ingest`, `/api/ov/replay`), never to Overseer directly: the proxy holds
 * the real ingest key, templates every path and scrubs every string. The `ingestKey: 'proxy'` below is a placeholder
 * the proxy ignores.
 *
 * Contract facts this file leans on (overseer-contract.md):
 *  - the SDK export is `initAnalytics`; calling it twice installs duplicate listeners, so init is guarded (once);
 *  - the session id is read from sessionStorage["__overseer_sid"] at init: we seed a DURABLE visitor id there first, so
 *    a visitor's funnel survives new tabs and return visits (the id rotates after 90 idle days);
 *  - identify() values must be string | null: a JSON number drops the whole batch, "" is stored as a real customer;
 *  - the SDK loads rrweb from `window.rrweb.record` first and its own import("rrweb") is bundler-ignored, so WE import
 *    rrweb lazily and publish it on window before init;
 *  - the SDK already emits pageview / route_change / click / js_error / 5xx network_error. Nothing here re-emits those.
 *
 * Every function is fail-silent: analytics must never break the app, and nothing here may leave a rejected promise.
 */
import type { OverseerConfig, OverseerHandle, OverseerIdentity } from '../vendor/overseer-sdk/index.mjs';
import { isOvSelector } from './overseerLabels';

// The closed vocabulary of click labels (data-ov) and the selector grammar live in their own data file; the labels are
// re-exported here so the whole vocabulary — funnels, steps, labels — is imported from one place.
export { OV_LABELS, isOvLabel, type OvLabel } from './overseerLabels';

declare global {
  interface Window {
    /** rrweb, published for the vendored SDK (it prefers window.rrweb.record over its own import). */
    rrweb?: unknown;
  }
}

// ── Closed vocabulary ───────────────────────────────────────────────────────────────────────────────────────────────
// Every funnel and step name that may leave the browser. Overseer funnels are cohorts: `<name>@<YYYY-MM>`. The
// `activation` cohort is deliberately absent — the server owns it. T3 (OVERSEER-111) greps every call site against
// this; ovStep() also drops anything outside it at runtime, so a stray free-text value can never become a step.

export const OV_CHECKOUT_PATHS = ['guest', 'signed_in', 'upgrade'] as const;
export const OV_DEVICES = ['phone', 'desktop'] as const;
export type OvCheckoutPath = (typeof OV_CHECKOUT_PATHS)[number];
export type OvDevice = (typeof OV_DEVICES)[number];

/** Per funnel base, the allowed values of each dotted segment: `checkout.<path>.<device>@M`, `preview.<device>@M`. */
const OV_FUNNEL_SHAPES = {
  checkout: [OV_CHECKOUT_PATHS, OV_DEVICES],
  checkout_errors: [],
  preview: [OV_DEVICES],
} as const;

export type OvFunnelBase = keyof typeof OV_FUNNEL_SHAPES;
export const OV_FUNNELS = Object.keys(OV_FUNNEL_SHAPES) as OvFunnelBase[];

export const OV_STEPS = [
  // checkout.<path>.<device>@M — one per wizard step, plus the two moments push() mirrors from analytics.ts, plus
  // `published` (FE-2: the visitor's site went live — credited to the checkout funnel they came through)
  'package', 'addons', 'domain', 'account', 'payment', 'confirmed', 'payment_submitted', 'purchased', 'published',
  // preview.<device>@M — an outreach prospect's visit to /preview/:token (`purchased` is shared with checkout)
  'viewed', 'site_click', 'offer_opened', 'get_started', 'get_this_website', 'book_demo',
  'depth_25', 'depth_50', 'depth_75', 'depth_100', 'dwell_15', 'dwell_60', 'dwell_180',
  // checkout_errors@M — closed reason codes (validation_<group> is open-ended, see isOvStep)
  'declined', 'avs', 'cvc', 'expired', 'duplicate', 'held', 'unavailable',
  'domain_taken', 'promo_invalid', 'email_exists', 'total_changed', 'timeout', 'server_error',
  'validation_payment',
] as const;
export type OvStep = (typeof OV_STEPS)[number] | `validation_${string}`;

const VALIDATION_STEP_RE = /^validation_[a-z0-9_]+$/;
const FUNNEL_NAME_RE = /^([a-z0-9_]+)((?:\.[a-z0-9_-]+)*)@\d{4}-\d{2}$/;

export function isOvStep(step: unknown): step is OvStep {
  return typeof step === 'string' && ((OV_STEPS as readonly string[]).includes(step) || VALIDATION_STEP_RE.test(step));
}

/** True for a well-formed `<base>[.<segment>…]@YYYY-MM` whose base and segments are all in the vocabulary. */
export function isOvFunnel(name: unknown): name is string {
  if (typeof name !== 'string') return false;
  const m = FUNNEL_NAME_RE.exec(name);
  if (!m) return false;
  const shape = (OV_FUNNEL_SHAPES as Record<string, readonly (readonly string[])[]>)[m[1]];
  if (!shape) return false;
  const segments = m[2] ? m[2].slice(1).split('.') : [];
  return segments.length === shape.length && segments.every((seg, i) => shape[i].includes(seg));
}

// ── Naming helpers ──────────────────────────────────────────────────────────────────────────────────────────────────

/** 'YYYY-MM' in UTC: the cohort month a funnel name carries. */
export function ovMonth(date: Date = new Date()): string {
  const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** 'phone' below 768 px viewport width (Tailwind's md breakpoint), else 'desktop'. */
export function ovDevice(): OvDevice {
  try {
    const w = typeof window !== 'undefined' ? window.innerWidth : 0;
    return typeof w === 'number' && w > 0 && w < 768 ? 'phone' : 'desktop';
  } catch {
    return 'desktop';
  }
}

/** `ovFunnel('checkout', 'guest', 'phone')` → 'checkout.guest.phone@2026-11'. Segments are lower-cased and sanitised. */
export function ovFunnel(base: OvFunnelBase, ...segments: string[]): string {
  const parts = [base, ...segments.filter((s) => s !== '' && s != null).map((s) => String(s).toLowerCase().replace(/[^a-z0-9_-]/g, '_'))];
  return `${parts.join('.')}@${ovMonth()}`;
}

// Order matters: the first rule that matches wins. The reason string is free text (a server error message, an
// exception message or a code such as 'tokenization_failed') — only the CODE it maps to is ever sent.
const has = (re: RegExp) => (text: string): boolean => re.test(text);
const CHECKOUT_ERROR_RULES: ReadonlyArray<[test: (text: string) => boolean, step: OvStep]> = [
  [has(/tokenization_failed/i), 'validation_payment'],
  [has(/time(?:d)?[\s-]?out|etimedout|abort/i), 'timeout'],
  [has(/\bavs\b|address verification/i), 'avs'],
  [has(/\bcvc\b|\bcvv\b|security code|card code/i), 'cvc'],
  [has(/(?:card|expiration|expiry)[^.]*expir|expir[^.]*\bcard\b|expiration date/i), 'expired'],
  [has(/duplicate/i), 'duplicate'],
  [has(/held for (?:manual )?review|under review|pending review/i), 'held'],
  // Before the domain rule: "already registered / in use" alone is ambiguous, "account … already" is not.
  [has(/\b(?:account|email)\b[^.]*\balready\b|email_exists/i), 'email_exists'],
  // The word "domain" and the qualifier can sit in different sentences ("X is no longer available. Choose a different domain.").
  // "completing a purchase of this domain" is the server's own wording while another buyer holds it (DOMAIN_BEING_PURCHASED).
  [
    (text) => (/\bdomain\b/i.test(text) && /taken|unavailable|no longer available|already|being purchased|completing a purchase|registered|in use/i.test(text))
      || /\baddress\b[^.]*\balready taken/i.test(text), // a free *.app address that is taken
    'domain_taken',
  ],
  [has(/declin|could not be processed/i), 'declined'],
  [has(/promo|coupon/i), 'promo_invalid'],
  [has(/total[^.]*(?:changed|differ)|total_changed|price[^.]*changed/i), 'total_changed'],
  [has(/unavailable|not configured/i), 'unavailable'],
];

/**
 * Maps a `sf_deploy_failed` reason onto the closed `checkout_errors` vocabulary. Anything unrecognised is
 * 'server_error'. Post-payment publish failures from ManagePage (`publish_*`) are not checkout errors: null.
 */
export function ovCheckoutErrorStep(reason: string): OvStep | null {
  const text = String(reason || '');
  if (/^publish_/i.test(text)) return null;
  for (const [matches, step] of CHECKOUT_ERROR_RULES) if (matches(text)) return step;
  return 'server_error';
}

// ── Constants ───────────────────────────────────────────────────────────────────────────────────────────────────────

const OV_COMPANY = 'c813';
const OV_APP = 'gigapress';
const OV_COLLECTOR = '/api/ov/ingest';
const OV_INGEST_KEY = 'proxy';
const OV_PROXY_PREFIX = '/api/ov/';

const SESSION_ID_KEY = '__overseer_sid'; // the SDK's own key — part of the contract
const VISITOR_KEY = 'sf_ov_vid'; // localStorage { id, lastSeen }
const STEPS_KEY = 'sf_ov_steps'; // localStorage { vid, done: ['funnel|step', …] }
const OPEN_KEY = 'sf_ov_open'; // localStorage { vid, checkout?, preview? } — the funnels this visitor is "in"
const VISITOR_IDLE_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_DONE = 400;
const MAX_PENDING = 50;
const MUTE_TENANT = 'mute:staff'; // the proxy drops every event of a `mute:*` tenant

// ── Module state (one SDK per page) ─────────────────────────────────────────────────────────────────────────────────

interface Identity {
  customerId: string | null;
  tenantId: string | null;
}

let handle: OverseerHandle | null = null;
let initPromise: Promise<boolean> | null = null;
let failed = false;
let muted = false;
let visitorId: string | null = null;
let desired: Identity = { customerId: null, tenantId: null };
let applied: Identity | null = null;
let fetchWrapped = false;
let stopFns: Array<() => void> = [];
const pending: Array<{ funnel: string; step: string }> = [];
const sentThisLoad = new Set<string>();
const openFunnels: { checkout: string | null; preview: string | null } = { checkout: null, preview: null };

// ── Storage (every access may throw: private mode, blocked cookies, quota) ──────────────────────────────────────────

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function newUuid(): string {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    if (c && typeof c.getRandomValues === 'function') {
      const b = c.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
      return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
    }
  } catch {
    // fall through
  }
  // No crypto at all (very old / non-secure context): an analytics visitor id needs uniqueness, not secrecy.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** The durable visitor id: kept while the visitor returns within 90 days, rotated after that. lastSeen is bumped. */
function resolveVisitorId(now: number): string {
  const rec = readJson<{ id?: unknown; lastSeen?: unknown }>(VISITOR_KEY);
  if (rec && typeof rec.id === 'string' && rec.id && typeof rec.lastSeen === 'number' && Number.isFinite(rec.lastSeen)
      && now - rec.lastSeen <= VISITOR_IDLE_MS) {
    writeJson(VISITOR_KEY, { id: rec.id, lastSeen: now });
    return rec.id;
  }
  const id = newUuid();
  if (!writeJson(VISITOR_KEY, { id, lastSeen: now })) {
    // localStorage is unusable: stay on this tab's existing session id if it has one, so a reload keeps its session.
    try {
      const existing = sessionStorage.getItem(SESSION_ID_KEY);
      if (existing) return existing;
    } catch {
      // nothing to reuse
    }
  }
  return id;
}

function peekVisitorId(): string | null {
  const rec = readJson<{ id?: unknown }>(VISITOR_KEY);
  return rec && typeof rec.id === 'string' && rec.id ? rec.id : null;
}

// ── Identity ────────────────────────────────────────────────────────────────────────────────────────────────────────

/** String id or null — never a number (drops the batch), never "" (a real empty customer) and never an email. */
function idOrNull(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s && !s.includes('@') ? s : null;
}

function sameIdentity(a: Identity | null, b: Identity): boolean {
  return !!a && a.customerId === b.customerId && a.tenantId === b.tenantId;
}

function setIdentity(next: Identity): void {
  desired = next;
  if (!handle || sameIdentity(applied, next)) return; // before init, config.identify() picks `desired` up
  try {
    const traits: OverseerIdentity = { customerId: next.customerId, tenantId: next.tenantId };
    handle.identify(traits);
    applied = { ...next };
  } catch {
    // the SDK never throws; belt and braces
  }
}

/**
 * Tie later events to the signed-in user: customerId = the user's UUID (never the email), tenantId = the partner id
 * for partner-owned accounts. Signed out → both null. Re-callable: only a change reaches the SDK. Also lifts a mute.
 */
export function identifyOverseer(user: { userId?: unknown; partnerId?: unknown } | null | undefined): void {
  try {
    muted = false;
    setIdentity({ customerId: idOrNull(user?.userId), tenantId: idOrNull(user?.partnerId) });
  } catch {
    // fail-silent
  }
}

/**
 * Staff, impersonated and QA sessions must leave no trace. Later events are tagged with the `mute:staff` tenant (the
 * proxy drops them), queued steps are discarded, and the rrweb recorder is stopped — replay uploads carry no identity,
 * so the proxy could not mute them itself. Safe before init (it just keeps the SDK from ever starting).
 */
export function muteOverseerForStaff(): void {
  try {
    muted = true;
    pending.length = 0;
    for (const stop of stopFns.splice(0)) {
      try { stop(); } catch { /* already stopped */ }
    }
    setIdentity({ customerId: null, tenantId: MUTE_TENANT });
  } catch {
    // fail-silent
  }
}

// ── Conversion steps ────────────────────────────────────────────────────────────────────────────────────────────────

function openKindOf(funnel: string): 'checkout' | 'preview' | null {
  if (funnel.startsWith('checkout.')) return 'checkout'; // checkout_errors@M has no dot, so it never matches
  if (funnel.startsWith('preview.')) return 'preview';
  return null;
}

/** Remember which checkout / preview funnel this visitor is in, so a later sf_purchase can credit it. */
function noteOpenFunnel(funnel: string): void {
  const kind = openKindOf(funnel);
  if (!kind) return;
  openFunnels[kind] = funnel;
  if (!visitorId) return; // persisted by drainPending/emitStep once the visitor id exists
  const rec = readJson<{ vid?: string }>(OPEN_KEY);
  writeJson(OPEN_KEY, { ...(rec && rec.vid === visitorId ? rec : {}), vid: visitorId, [kind]: funnel });
}

/** The last `checkout.…` / `preview.…` funnel this browser recorded a step in, or null. */
export function ovOpenFunnel(kind: 'checkout' | 'preview'): string | null {
  try {
    if (openFunnels[kind]) return openFunnels[kind];
    const rec = readJson<Record<string, unknown>>(OPEN_KEY);
    const vid = visitorId || peekVisitorId();
    const value = rec && vid && rec.vid === vid ? rec[kind] : null;
    return typeof value === 'string' && isOvFunnel(value) ? value : null;
  } catch {
    return null;
  }
}

function emitStep(funnel: string, step: string): void {
  if (!handle) return;
  const key = `${funnel}|${step}`;
  const rec = readJson<{ vid?: string; done?: unknown }>(STEPS_KEY);
  const done = rec && rec.vid === visitorId && Array.isArray(rec.done) ? rec.done.filter((x): x is string => typeof x === 'string') : [];
  if (done.includes(key) || sentThisLoad.has(key)) return; // once per visitor + funnel + step
  sentThisLoad.add(key);
  writeJson(STEPS_KEY, { vid: visitorId, done: [...done, key].slice(-MAX_DONE) });
  handle.track('conversion_step', { funnel, step });
}

function drainPending(): void {
  for (const item of pending.splice(0)) emitStep(item.funnel, item.step);
}

/**
 * One conversion step of a funnel cohort, e.g. ovStep(ovFunnel('checkout', 'guest', ovDevice()), 'domain'). Sent as a
 * raw `conversion_step {funnel, step}`, once per visitor + funnel + step. Names outside the closed vocabulary are
 * dropped. Called before the SDK is running (it loads lazily) the step waits in a small queue; if the session turns
 * out to be muted or init fails, the queue is discarded.
 */
export function ovStep(funnel: string, step: OvStep): void {
  try {
    if (muted || failed) return;
    if (!isOvFunnel(funnel) || !isOvStep(step)) return;
    noteOpenFunnel(funnel);
    if (!handle) {
      if (!pending.some((p) => p.funnel === funnel && p.step === step)) {
        if (pending.length >= MAX_PENDING) pending.shift();
        pending.push({ funnel, step });
      }
      return;
    }
    emitStep(funnel, step);
  } catch {
    // fail-silent
  }
}

// ── Clean clicks ────────────────────────────────────────────────────────────────────────────────────────────────────
// The SDK already emits a raw `click` / `rage_click` / `dead_click` for every click, with a selector built from the
// element's tag and CSS class — which the proxy drops. What survives the proxy is a selector that starts `sf:`, so the
// controls we name (utils/overseerClicks.ts, utils/overseerPreviewFrame.ts) reach it through this one helper. The page
// path (`url`) is put on the event by the SDK and templated by the proxy: it is never set here.

export type OvClickKind = 'click' | 'rage_click' | 'dead_click';
const OV_CLICK_KINDS: readonly string[] = ['click', 'rage_click', 'dead_click'];
// The selector must be one the proxy keeps (isOvSelector: `sf:` + letters, digits and `_ . : -`, starting with a letter or
// digit): no whitespace, `@`, `?`, `=`, `%` or `/`, so neither visible text nor an email nor a query string can ride along.

/**
 * One click-family event with a clean selector (`sf:<label>`), gated like ovStep: nothing until the SDK is running,
 * nothing while the session is muted or init failed. Unlike ovStep it does not queue — a click that happened before the
 * SDK started is not worth replaying. Returns whether the event was handed to the SDK, so a caller can skip the work
 * that only matters for a recorded click (rage / dead-click bookkeeping). Never throws.
 */
export function ovTrackClick(kind: OvClickKind, selector: string): boolean {
  try {
    if (muted || failed || !handle) return false;
    if (!OV_CLICK_KINDS.includes(kind)) return false;
    if (!isOvSelector(selector)) return false;
    handle.track(kind, { selector });
    return true;
  } catch {
    return false;
  }
}

// ── 4xx capture ─────────────────────────────────────────────────────────────────────────────────────────────────────
// The SDK reports only 5xx and failed fetches. A declined card, a taken email or a refused upgrade is a 4xx, and those
// are exactly the checkout failures worth seeing, so the money paths below are wrapped to report them too.

const FOUR_XX_PATHS: readonly RegExp[] = [
  /^\/api\/billing\/checkout\/?$/,
  /^\/api\/auth\/pre-register\/?$/,
  /^\/api\/auth\/register\/?$/,
  /^\/api\/payment-links\/[^/]+\/pay\/?$/,
  /^\/api\/billing\/orders\/[^/]+\/upgrade\/?$/,
];

function describeRequest(input: unknown, init: RequestInit | undefined): { path: string; method: string } | null {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as { url?: string } | null)?.url;
    if (!raw) return null;
    const base = typeof location !== 'undefined' && location.href ? location.href : 'http://localhost/';
    const url = new URL(raw, base);
    if (typeof location !== 'undefined' && location.origin && url.origin !== location.origin) return null;
    const method = String(init?.method || (input as { method?: string } | null)?.method || 'GET').toUpperCase();
    return { path: url.pathname, method };
  } catch {
    return null;
  }
}

/**
 * Wraps window.fetch ONCE so an allow-listed money path that answers 400–499 is reported as
 * `network_error {endpoint (path only), method, status}`. Never for /api/ov/* (our own beacons), never reads a body,
 * never alters the response or the rejection the caller sees. Inert until the SDK is running and while muted.
 */
export function installOverseer4xxCapture(): void {
  try {
    if (fetchWrapped || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
    fetchWrapped = true;
    const original = window.fetch;
    window.fetch = function overseerFetch(this: unknown, input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const promise = original.call(this, input, init);
      try {
        promise.then(
          (res) => {
            try {
              if (!handle || muted || !res || res.status < 400 || res.status >= 500) return;
              const req = describeRequest(input, init);
              if (!req || req.path.startsWith(OV_PROXY_PREFIX)) return;
              if (!FOUR_XX_PATHS.some((re) => re.test(req.path))) return;
              handle.track('network_error', { endpoint: req.path, method: req.method, status: res.status });
            } catch {
              // fail-silent
            }
          },
          () => { /* the caller handles its own rejection; the SDK reports failed fetches itself */ },
        );
      } catch {
        // fail-silent
      }
      return promise;
    };
  } catch {
    // fail-silent
  }
}

// ── Init ────────────────────────────────────────────────────────────────────────────────────────────────────────────

type SdkModule = { initAnalytics: (config: OverseerConfig) => OverseerHandle };

export interface InitOverseerOptions {
  /** Test seams — the defaults import the lazy chunks. */
  loadSdk?: () => Promise<SdkModule>;
  loadRrweb?: () => Promise<unknown>;
  now?: () => number;
}

const defaultLoadSdk = (): Promise<SdkModule> => import('../vendor/overseer-sdk/index.mjs');
const defaultLoadRrweb = (): Promise<unknown> => import('rrweb');

/** Publish rrweb on window for the SDK, remembering each recorder's stop function so a mute can end the recording. */
function publishRrweb(mod: unknown): void {
  const ns = mod as { record?: unknown; default?: { record?: unknown } } | null;
  const source = typeof ns?.record === 'function' ? ns : typeof ns?.default?.record === 'function' ? ns.default : null;
  if (!source) return; // replay quietly stays off, exactly as if rrweb were missing
  const realRecord = source.record as (...args: unknown[]) => unknown;
  const record = (...args: unknown[]): unknown => {
    if (muted) return undefined;
    const stop = realRecord(...args);
    if (typeof stop === 'function') stopFns.push(stop as () => void);
    return stop;
  };
  window.rrweb = Object.assign({}, source, { record });
}

async function runInit(opts: InitOverseerOptions): Promise<boolean> {
  try {
    if (typeof window === 'undefined') {
      failed = true;
      return false;
    }
    visitorId = resolveVisitorId((opts.now ?? Date.now)());
    try {
      sessionStorage.setItem(SESSION_ID_KEY, visitorId); // read by the SDK at init — must be seeded first
    } catch {
      // blocked storage: the SDK falls back to a per-page-load id
    }
    const [rrweb, sdk] = await Promise.all([(opts.loadRrweb ?? defaultLoadRrweb)().catch(() => null), (opts.loadSdk ?? defaultLoadSdk)()]);
    if (muted) {
      initPromise = null; // a staff / QA identity arrived while the chunks loaded: don't start, allow a later retry
      return false;
    }
    publishRrweb(rrweb);
    handle = sdk.initAnalytics({
      company: OV_COMPANY,
      app: OV_APP,
      collector: OV_COLLECTOR,
      ingestKey: OV_INGEST_KEY,
      replay: { enabled: true, sampleRate: 1 },
      identify: () => ({ customerId: desired.customerId, tenantId: desired.tenantId }),
    });
    applied = { ...desired };
    installOverseer4xxCapture();
    for (const kind of ['checkout', 'preview'] as const) if (openFunnels[kind]) noteOpenFunnel(openFunnels[kind] as string);
    drainPending();
    return true;
  } catch {
    failed = true;
    pending.length = 0;
    return false;
  }
}

/**
 * Starts the SDK — once per page. Resolves true when it is running, false if it was skipped or failed (never rejects).
 * The caller decides WHETHER to start (utils/overseerGate.ts); this only does it. Replay is on for every session.
 */
export function initOverseer(opts: InitOverseerOptions = {}): Promise<boolean> {
  if (!initPromise) initPromise = runInit(opts).catch(() => false);
  return initPromise;
}
